import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { login as authLogin, getProfile, updateProfile } from '../../api/modules/auth'
import mitt from 'mitt'
import axios from 'axios'

// 创建事件总线
export const emitter = mitt()

export const useUserStore = defineStore('user', () => {
  // 状态
  const token = ref(localStorage.getItem('token') || '')
  const username = ref(localStorage.getItem('username') || '')
  const nickname = ref(localStorage.getItem('nickname') || '')
  const avatar = ref(localStorage.getItem('avatar') || '')
  const roles = ref(JSON.parse(localStorage.getItem('roles') || '[]'))
  const permissions = ref(JSON.parse(localStorage.getItem('permissions') || '[]'))
  const userInfo = ref({
    id: null,
    name: '',
    nickname: '',
    phone: '',
    idCard: '',
    qq: '',
    bankCard: '',
    bankName: '',
    address: '',
    isvip: null // null 表示未加载，1 表示 VIP，其他值表示非 VIP
  })

  // 计算属性
  const isLoggedIn = computed(() => !!token.value)
  const isAdmin = computed(() => roles.value.includes('admin'))
  const userId = computed(() => userInfo.value.id)
  const displayName = computed(() => nickname.value || username.value)
  // 是否可以访问音视频处理功能（isvip 为 1 时可以使用）
  // 如果 isvip 为 null 或 undefined，默认不允许访问（等待用户信息加载后再判断）
  const canAccessMediaProcessing = computed(() => {
    const isvip = userInfo.value.isvip
    // 只有当 isvip 明确为 1 时才允许访问
    // 如果 isvip 为 null 或 undefined，返回 false（不允许访问，等待用户信息加载）
    const result = isvip === 1
    console.log('canAccessMediaProcessing 计算:', { isvip, result })
    return result
  })

  // 方法
  function setToken(value) {
    token.value = value
    localStorage.setItem('token', value)
  }

  function setUsername(value) {
    username.value = value
    localStorage.setItem('username', value)
  }

  function setNickname(value) {
    nickname.value = value
    localStorage.setItem('nickname', value)
  }

  function setAvatar(value) {
    avatar.value = value
    localStorage.setItem('avatar', value)
  }

  function setRoles(value) {
    roles.value = value
    localStorage.setItem('roles', JSON.stringify(value))
  }
  
  function setPermissions(value) {
    permissions.value = value
    localStorage.setItem('permissions', JSON.stringify(value))
  }
  
  // 检查用户名是否包含中文
  function containsChinese(str) {
    return /[\u4e00-\u9fa5]/.test(str)
  }
  
  // 初始化用户存储，修复可能错误的用户名
  function initializeUserStore() {
    console.log('初始化用户存储...')
    const storedUsername = localStorage.getItem('username')
    
    // 如果登录且用户名包含中文，可能是昵称被错误地设置为了用户名
    if (storedUsername && containsChinese(storedUsername) && token.value) {
      console.log('检测到用户名可能是昵称，尝试修复...')
      
      // 查询用户信息以获取正确的用户名
      fetchUserInfo()
        .then(userData => {
          if (userData && userData.username && !containsChinese(userData.username)) {
            console.log('从用户信息中找到了正确的用户名:', userData.username)
            // 设置正确的用户名
            setUsername(userData.username)
            // 设置昵称（如果有）
            if (userData.nickname) {
              setNickname(userData.nickname)
            }
          } else {
            console.log('无法从用户信息中找到正确的用户名')
          }
        })
        .catch(error => {
          console.error('获取用户信息失败，无法修复用户名:', error)
        })
    } else {
      console.log('用户名检查通过，不需要修复')
    }
  }
  
  // 检查是否有指定权限
  function hasPermission(permission) {
    // 管理员拥有所有权限
    if (isAdmin.value) {
      return true
    }
    
    return permissions.value.includes(permission)
  }

  // 获取用户信息方法
  async function fetchUserInfo() {
    try {
      console.log('获取用户个人信息')
      // 检查是否有令牌
      const currentToken = localStorage.getItem('token')
      if (!currentToken) {
        console.error('获取用户信息失败: 没有有效的认证令牌')
        throw new Error('没有有效的认证令牌')
      }
      
      const result = await getProfile()
      console.log('用户个人信息获取成功，原始数据:', result)
      
      // 确保返回了有效的用户对象
      if (!result || !result.user) {
        console.error('获取到的用户信息无效:', result)
        throw new Error('用户信息无效')
      }
      
      // 检查必要的字段
      if (!result.user.id) {
        console.error('获取到的用户信息缺少ID字段，完整数据:', result)
        throw new Error('用户信息无效：缺少ID字段')
      }
      
      // 仅当接口明确返回 permissions 数组时才覆盖本地权限；缺省字段不应用 [] 误清空（304/旧缓存常见问题）
      const profilePerms = result.user.permissions
      const resolvedPermissions = Array.isArray(profilePerms)
        ? profilePerms
        : [...permissions.value]

      // 构建用户信息对象
      const userData = {
        id: result.user.id,
        name: result.user.name || '',
        nickname: result.user.nickname || '',
        phone: result.user.phone || '',
        idCard: result.user.idCard || '',
        qq: result.user.qq || '',
        bankCard: result.user.bankCard || '',
        bankName: result.user.bankName || '',
        address: result.user.address || '',
        avatar: result.user.avatar || '',
        role: result.user.role || '',
        username: result.user.username || '',
        email: result.user.email || '',
        created_at: result.user.created_at || '',
        permissions: resolvedPermissions,
        isvip: result.user.isvip !== undefined ? result.user.isvip : null
      }
      
      console.log('构建的用户信息对象:', userData)
      console.log('isvip 值:', userData.isvip)
      console.log('canAccessMediaProcessing 将返回:', userData.isvip === 1)
      
      // 更新store中的用户信息
      userInfo.value = userData
      if (Array.isArray(profilePerms)) {
        setPermissions(profilePerms)
      }
      
      // 将用户信息保存到 localStorage，方便路由守卫检查
      localStorage.setItem('userInfo', JSON.stringify(userData))
      
      // 如果返回的信息包含昵称，单独设置昵称，不影响用户名
      if (userData.nickname) {
        setNickname(userData.nickname)
      }

      // 如果返回的信息包含用户名且当前用户名可能是昵称，更新用户名
      if (userData.username && (!username.value || containsChinese(username.value))) {
        setUsername(userData.username)
      }
      
      return userData
    } catch (error) {
      console.error('获取用户个人信息失败:', error)
      // 如果是认证错误，清理本地数据
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        // 清理无效令牌和用户数据
        logout()
      }
      throw error
    }
  }

  async function login(credentials) {
    console.log('开始登录流程，用户名:', credentials.username)
    try {
      const response = await authLogin(credentials.username, credentials.password)
      console.log('登录API响应:', response)
      const { token, user } = response
      // 保存token
      setToken(token)
      console.log('Token已保存')
      // 设置用户名 - 使用实际用户名而非昵称
      setUsername(user.username)
      // 单独设置昵称，如果存在
      if (user.nickname) {
        setNickname(user.nickname)
      }
      // 设置角色
      setRoles([user.role])
      if (Array.isArray(user.permissions)) {
        setPermissions(user.permissions)
        console.log('权限已保存:', user.permissions)
      }
      // 设置用户信息
      const userData = {
        id: user.id,
        name: user.name || '',
        nickname: user.nickname || '',
        phone: user.phone || '',
        idCard: user.idCard || '',
        qq: user.qq || '',
        bankCard: user.bankCard || '',
        bankName: user.bankName || '',
        address: user.address || '',
        created_at: user.created_at || '',
        isvip: user.isvip !== undefined ? user.isvip : null
      }
      userInfo.value = userData
      
      // 将用户信息保存到 localStorage，方便路由守卫检查
      localStorage.setItem('userInfo', JSON.stringify(userData))
      return user
    } catch (error) {
      console.error('登录失败:', error)
      throw error
    }
  }

  function logout() {
    setToken('')
    setUsername('')
    setNickname('')
    setAvatar('')
    setRoles([])
    setPermissions([])
    
    // 清除localStorage中的所有相关数据
    localStorage.removeItem('token')
    localStorage.removeItem('username')
    localStorage.removeItem('nickname')
    localStorage.removeItem('avatar')
    localStorage.removeItem('roles')
    localStorage.removeItem('permissions')
    localStorage.removeItem('userPermissions')
    localStorage.removeItem('userRoles')
  }

  // 更新用户信息方法
  async function updateUserInfo(newUserInfo) {
    try {
      console.log('准备更新的用户信息:', newUserInfo)
      console.log('更新前的store中用户信息:', userInfo.value)
      
      // 更新到服务器
      const result = await updateProfile(newUserInfo)
      console.log('服务器返回的更新结果:', result)
      
      // 更新本地状态，确保所有字段都被正确更新
      if (result && result.user) {
        console.log('使用服务器返回的用户信息更新store:', result.user)
        // 完全替换用户信息，而不是合并
        userInfo.value = {
          ...result.user
        }
        
        // 如果更新了昵称，只更新昵称，不影响用户名
        if (result.user.nickname) {
          setNickname(result.user.nickname)
        }
        
        // 发出用户信息更新事件
        emitter.emit('userInfoUpdated', userInfo.value)
      } else {
        console.error('服务器返回的更新结果中没有用户信息')
        throw new Error('更新失败：服务器返回数据无效')
      }
      
      return result
    } catch (error) {
      console.error('更新用户信息失败:', error)
      throw error
    }
  }

  // 立即调用初始化函数
  initializeUserStore()

  return {
    token,
    username,
    nickname,
    displayName,
    avatar,
    roles,
    permissions,
    userInfo,
    userId,
    isLoggedIn,
    isAdmin,
    canAccessMediaProcessing,
    setToken,
    setUsername,
    setNickname,
    setAvatar,
    setRoles,
    setPermissions,
    login,
    logout,
    fetchUserInfo,
    updateUserInfo,
    hasPermission,
    initializeUserStore
  }
}) 